import * as React from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { useIsICRAResearchAssistBranding } from "@needl-ai/common/branding";
import { useFetchFeatureConfig } from "@needl-ai/common/hooks/useFetchFeatureConfig";
import { isMSquaredHub } from "@needl-ai/common/utils/isEnv";
import {
  useGetPathPrefix,
  WorkspaceSwitcherButton,
} from "@needl-ai/common/workspace";

import SearchHistoryModal from "./modals/SearchHistoryModal";
import { ArtifactReportButton } from "./sidebar/ArtifactReportButton";
import ChatSessionsList from "./sidebar/ChatSessionsList";
import IntegrationsButton from "./sidebar/IntegrationsButton";
import NewChatButton from "./sidebar/NewChatButton";
import SearchChatButton from "./sidebar/SearchChatButton";

import { useFileUploadContext } from "@/contexts/FileUploadContext";
import { useWelcomeContent } from "@/contexts/UIContext";
import { useAskNeedlUpload } from "@/hooks/useAskNeedlUpload";
import { useChat } from "@/hooks/useChat";
import {
  useDeleteSession,
  useFetchAllSession,
} from "@/hooks/useCreateUpdateSession";
import { hasIncompleteAttachments } from "@/utils/attachmentUtils";
import { AppSidebar, useSidebar } from "@/vendor/AppSidebar";

const Sidebar = () => {
  const { state: sidebarState } = useSidebar();
  const navigate = useNavigate();
  const pathPrefix = useGetPathPrefix();
  const location = useLocation();
  const { initializeChatState, sessionId, isLoading, asyncStatuses } =
    useChat();
  const { state } = useFileUploadContext();
  const { clearAttachments } = useAskNeedlUpload();
  const { setShowWelcomeContent } = useWelcomeContent();
  const isICRAResearchAssist = useIsICRAResearchAssistBranding();
  const isMSquaredHubEnv = isMSquaredHub();
  const { data: featureConfig } = useFetchFeatureConfig();
  const showArtifactReport = featureConfig?.artifact_report ?? false;

  const deleteSessionMutation = useDeleteSession();
  const { data: sessions = [], isLoading: sessionsLoading } =
    useFetchAllSession({
      askneedl_type: "SUPERCHARGING_ASKNEEDL",
    });
  const _isOnLandingPage = location.pathname === "/";
  const hasIncompleteFiles = hasIncompleteAttachments(state.attachments);

  const isSidebarOpen = sidebarState === "expanded";

  const [isSearchModalOpen, setIsSearchModalOpen] = React.useState(false);

  const handleNewConsultation = React.useCallback(() => {
    setShowWelcomeContent(false);
    clearAttachments();
    initializeChatState();
    navigate(`${pathPrefix}/`);
  }, [
    clearAttachments,
    initializeChatState,
    navigate,
    pathPrefix,
    setShowWelcomeContent,
  ]);

  const handleSelectSession = React.useCallback(
    (id: string) => {
      clearAttachments();
      initializeChatState();
      navigate(`${pathPrefix}/c/${id}`, { replace: true });
    },
    [clearAttachments, initializeChatState, navigate, pathPrefix]
  );

  const handleDeleteSession = React.useCallback(
    async (id: string) => {
      if (
        window.confirm(
          "Are you sure you want to delete this chat session? This action cannot be undone."
        )
      ) {
        const isCurrentSession = id === sessionId;
        deleteSessionMutation.mutate(id);
        if (isCurrentSession) {
          initializeChatState();
          navigate(`${pathPrefix}/`);
        }
      }
    },
    [
      sessionId,
      deleteSessionMutation,
      initializeChatState,
      navigate,
      pathPrefix,
    ]
  );

  const handleSearchChat = React.useCallback(() => {
    if (isLoading || hasIncompleteFiles) {
      return;
    }
    setIsSearchModalOpen(true);
  }, [isLoading, hasIncompleteFiles]);

  const handleCloseSearchModal = React.useCallback(() => {
    setIsSearchModalOpen(false);
  }, []);

  return (
    <>
      <AppSidebar>
        <div className="py-4 flex-shrink-0">
          {!isMSquaredHubEnv && !isICRAResearchAssist && (
            <WorkspaceSwitcherButton
              isLoading={isLoading}
              hasIncompleteAttachments={hasIncompleteFiles}
              isCollapsed={!isSidebarOpen}
            />
          )}
          <NewChatButton
            onNewChat={handleNewConsultation}
            hasActiveConversation={true}
            isLoading={isLoading}
            hasIncompleteAttachments={hasIncompleteFiles}
            isCollapsed={!isSidebarOpen}
          />
          <SearchChatButton
            onSearchChat={handleSearchChat}
            isLoading={isLoading}
            hasIncompleteAttachments={hasIncompleteFiles}
            isCollapsed={!isSidebarOpen}
          />
          {!isMSquaredHubEnv && !isICRAResearchAssist && (
            <IntegrationsButton
              isLoading={isLoading}
              hasIncompleteAttachments={hasIncompleteFiles}
              isCollapsed={!isSidebarOpen}
            />
          )}
          {showArtifactReport && (
            <ArtifactReportButton
              isLoading={isLoading}
              hasIncompleteAttachments={hasIncompleteFiles}
              isCollapsed={!isSidebarOpen}
            />
          )}
        </div>
        {isSidebarOpen && (
          <div className="flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent dark:scrollbar-thumb-transparent">
            <ChatSessionsList
              sessions={sessions}
              sessionsLoaded={!sessionsLoading}
              sessionId={sessionId}
              onSelectSession={handleSelectSession}
              onDeleteSession={handleDeleteSession}
              isLoading={isLoading}
              hasIncompleteAttachments={hasIncompleteFiles}
              asyncStatuses={asyncStatuses}
            />
          </div>
        )}
      </AppSidebar>
      <SearchHistoryModal
        isOpen={isSearchModalOpen}
        onClose={handleCloseSearchModal}
      />
    </>
  );
};

export default Sidebar;
